import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Navbar from '../components/Navbar';

const AdminRecordingsPanel = () => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSessions, setSelectedSessions] = useState([]);
  
  // Filters
  const [filterQuestion, setFilterQuestion] = useState('');
  const [filterLanguage, setFilterLanguage] = useState('');
  const [filterFeatured, setFilterFeatured] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  useEffect(() => {
    if (user && user.role === 'ADMIN') {
      loadSessions();
      loadQuestions();
      loadStatistics();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filterQuestion, filterLanguage, filterFeatured, currentPage]);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: currentPage,
        size: 20
      });
      
      if (filterQuestion) params.append('questionId', filterQuestion);
      if (filterLanguage) params.append('language', filterLanguage);
      if (filterFeatured) params.append('featured', filterFeatured);
      
      const response = await api.get(`/api/ghost-coding/admin/sessions?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSessions(response.data.sessions);
      setTotalPages(response.data.totalPages);
      setTotalElements(response.data.totalElements);
    } catch (error) {
      console.error('Failed to load sessions:', error);
      alert('Failed to load sessions: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const loadQuestions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/api/questions', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestions(response.data);
    } catch (error) {
      console.error('Failed to load questions:', error);
    }
  };

  const loadStatistics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/api/ghost-coding/admin/statistics', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatistics(response.data);
    } catch (error) {
      console.error('Failed to load statistics:', error);
    }
  };

  const viewSessionDetails = async (session) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/api/ghost-coding/admin/sessions/${session.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedSession(response.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Failed to load session details:', error);
      alert('Failed to load session details');
    }
  };

  const toggleFeature = async (sessionId, currentFeatured) => {
    try {
      const token = localStorage.getItem('token');
      await api.post(
        `/api/ghost-coding/sessions/${sessionId}/feature`,
        { featured: !currentFeatured },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadSessions();
      alert(currentFeatured ? 'Session unfeatured successfully' : 'Session featured successfully!');
    } catch (error) {
      console.error('Failed to toggle feature:', error);
      alert('Failed to update session');
    }
  };

  const deleteSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this session? This cannot be undone.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/api/ghost-coding/admin/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadSessions();
      loadStatistics();
      alert('Session deleted successfully');
    } catch (error) {
      console.error('Failed to delete session:', error);
      alert('Failed to delete session');
    }
  };

  const bulkDelete = async () => {
    if (selectedSessions.length === 0) {
      alert('Please select sessions to delete');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${selectedSessions.length} sessions? This cannot be undone.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await api.post(
        '/api/ghost-coding/admin/sessions/bulk-delete',
        { sessionIds: selectedSessions },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedSessions([]);
      loadSessions();
      loadStatistics();
      alert('Sessions deleted successfully');
    } catch (error) {
      console.error('Failed to bulk delete:', error);
      alert('Failed to delete sessions');
    }
  };

  const toggleSelection = (sessionId) => {
    setSelectedSessions(prev => 
      prev.includes(sessionId) 
        ? prev.filter(id => id !== sessionId)
        : [...prev, sessionId]
    );
  };

  const selectAll = () => {
    if (selectedSessions.length === sessions.length) {
      setSelectedSessions([]);
    } else {
      setSelectedSessions(sessions.map(s => s.id));
    }
  };

  if (!user || user.role !== 'ADMIN') {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center bg-gray-50 pt-24">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h2>
            <p className="text-gray-600">You need admin privileges to access this page.</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 pt-24 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">👻 Ghost Coding Admin Panel</h1>
          <p className="text-gray-600">Manage all ghost coding recordings</p>
        </div>

        {/* Statistics */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-3xl font-bold text-blue-600">{statistics.totalSessions}</div>
              <div className="text-sm text-gray-600 mt-1">Total Sessions</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-3xl font-bold text-yellow-600">{statistics.featuredSessions}</div>
              <div className="text-sm text-gray-600 mt-1">Featured Sessions</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-3xl font-bold text-green-600">{statistics.totalViews}</div>
              <div className="text-sm text-gray-600 mt-1">Total Views</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-3xl font-bold text-purple-600">{statistics.totalLikes}</div>
              <div className="text-sm text-gray-600 mt-1">Total Likes</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Question</label>
              <select
                value={filterQuestion}
                onChange={(e) => { setFilterQuestion(e.target.value); setCurrentPage(0); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Questions</option>
                {questions.map(q => (
                  <option key={q.id} value={q.id}>{q.title}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
              <select
                value={filterLanguage}
                onChange={(e) => { setFilterLanguage(e.target.value); setCurrentPage(0); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Languages</option>
                <option value="java">Java</option>
                <option value="python">Python</option>
                <option value="javascript">JavaScript</option>
                <option value="cpp">C++</option>
                <option value="c">C</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Featured</label>
              <select
                value={filterFeatured}
                onChange={(e) => { setFilterFeatured(e.target.value); setCurrentPage(0); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All</option>
                <option value="true">Featured Only</option>
                <option value="false">Not Featured</option>
              </select>
            </div>
            
            <div className="flex items-end">
              <button
                onClick={() => { setFilterQuestion(''); setFilterLanguage(''); setFilterFeatured(''); setCurrentPage(0); }}
                className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-medium"
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedSessions.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex items-center justify-between">
            <span className="text-yellow-800 font-medium">
              {selectedSessions.length} session(s) selected
            </span>
            <button
              onClick={bulkDelete}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium"
            >
              Delete Selected
            </button>
          </div>
        )}

        {/* Sessions Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">
              Recordings ({totalElements})
            </h2>
            <button
              onClick={selectAll}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              {selectedSessions.length === sessions.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading sessions...</p>
            </div>
          ) : sessions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No recordings found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Select
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Question
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Language
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quality
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stats
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sessions.map(session => (
                    <tr key={session.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedSessions.includes(session.id)}
                          onChange={() => toggleSelection(session.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{session.questionTitle}</div>
                        {session.isFeatured && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                            ⭐ Featured
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {session.userName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          {session.language}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {session.codeSmellScore ? (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            session.codeSmellScore > 80 ? 'bg-green-100 text-green-800' :
                            session.codeSmellScore > 60 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {Math.round(session.codeSmellScore)}/100
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">N/A</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div>👁️ {session.viewsCount || 0}</div>
                        <div>👍 {session.likesCount || 0}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(session.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => viewSessionDetails(session)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          View
                        </button>
                        <button
                          onClick={() => toggleFeature(session.id, session.isFeatured)}
                          className={session.isFeatured ? 'text-yellow-600 hover:text-yellow-900' : 'text-gray-600 hover:text-gray-900'}
                        >
                          {session.isFeatured ? 'Unfeature' : 'Feature'}
                        </button>
                        <button
                          onClick={() => deleteSession(session.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {currentPage * 20 + 1} to {Math.min((currentPage + 1) * 20, totalElements)} of {totalElements} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                  disabled={currentPage === 0}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-sm text-gray-700">
                  Page {currentPage + 1} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                  disabled={currentPage >= totalPages - 1}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Detail Modal */}
        {showDetailModal && selectedSession && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowDetailModal(false)}
          >
            <div
              className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{selectedSession.questionTitle}</h2>
                    <p className="text-gray-600 mt-1">by {selectedSession.userName}</p>
                  </div>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="text-gray-400 hover:text-gray-600 text-2xl"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Language</label>
                    <p className="text-lg text-gray-900">{selectedSession.language}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Code Quality</label>
                    <p className="text-lg text-gray-900">
                      {selectedSession.codeSmellScore ? `${Math.round(selectedSession.codeSmellScore)}/100` : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Views</label>
                    <p className="text-lg text-gray-900">{selectedSession.viewsCount || 0}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Likes</label>
                    <p className="text-lg text-gray-900">{selectedSession.likesCount || 0}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Keystrokes</label>
                    <p className="text-lg text-gray-900">{selectedSession.keystrokeCount || 0}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Recording Events</label>
                    <p className="text-lg text-gray-900">{selectedSession.eventCount || 0}</p>
                  </div>
                </div>

                {selectedSession.finalCode && (
                  <div>
                    <label className="text-sm font-medium text-gray-500 mb-2 block">Final Code</label>
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                      {selectedSession.finalCode}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </>
  );
};

export default AdminRecordingsPanel;

